import {
  ExploreColumnType,
  isExploreColumnType,
} from "./columns/ExploreColum.model";

export type ComparisonOperators =
  | "eq"
  | "ne"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "in"
  | "nin"
  | "isnull"
  | "isnotnull"
  | "like"
  | "contains";

export type FieldFilterValue =
  | string
  | number
  | boolean
  | Date
  | string[]
  | number[];
export interface FieldFilter {
  [field: string]: Partial<Record<ComparisonOperators, FieldFilterValue>>;
}

export interface TupleFilter {
  tuple: {
    fields: string[];
    op: ComparisonOperators;
    values: Array<string | number | boolean | Date>;
  };
}

function isTupleFilter(filter: Filter): filter is TupleFilter {
  return "tuple" in filter;
}

export interface AndFilter {
  and: Filter[];
}

export function isAndFilter(filter: Filter): filter is AndFilter {
  return "and" in filter;
}

export interface OrFilter {
  or: Filter[];
}

function isOrFilter(filter: Filter): filter is OrFilter {
  return "or" in filter;
}

export function isFieldFilter(filter: Filter): filter is FieldFilter {
  return !isTupleFilter(filter) && !isAndFilter(filter) && !isOrFilter(filter);
}

export type Filter = FieldFilter | TupleFilter | AndFilter | OrFilter;

export function mergeFilter(filterA: Filter, filterB: Filter): Filter {
  if (isAndFilter(filterA) && isAndFilter(filterB)) {
    return {
      and: [...filterA.and, ...filterB.and],
    };
  }

  if (isOrFilter(filterA) && isOrFilter(filterB)) {
    return {
      or: [...filterA.or, ...filterB.or],
    };
  }

  if (isTupleFilter(filterA) && isTupleFilter(filterB)) {
    return {
      tuple: {
        fields: [...filterA.tuple.fields, ...filterB.tuple.fields],
        op: filterA.tuple.op,
        values: [...filterA.tuple.values, ...filterB.tuple.values],
      },
    };
  }

  return { and: [filterA, filterB] };
}

export function extractColumnsFromFilter(filter: Filter): ExploreColumnType[] {
  const columns: ExploreColumnType[] = [];

  if (isTupleFilter(filter)) {
    filter.tuple.fields.forEach((field) => {
      if (isExploreColumnType(field)) {
        columns.push(field);
      }
    });
  } else if (isAndFilter(filter)) {
    filter.and.forEach((f) => columns.push(...extractColumnsFromFilter(f)));
  } else if (isOrFilter(filter)) {
    filter.or.forEach((f) => columns.push(...extractColumnsFromFilter(f)));
  } else {
    // FieldFilter case
    Object.keys(filter).forEach((field) => {
      if (isExploreColumnType(field)) {
        columns.push(field);
      }
    });
  }

  return [...new Set(columns)]; // Remove duplicates
}
